import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Zap, Target, ChevronDown } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { StructuredData } from "@/components/StructuredData";
import { WebsiteSchema } from "@/components/WebsiteSchema";
import { BreadcrumbSchema } from "@/components/BreadcrumbSchema";
import { LazyVideoCard } from "@/components/LazyVideoCard";
import { BannerStrip } from "@/components/BannerStrip";
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { pushNotify } from "@/lib/pushNotify";
import { toast } from "sonner";
import { HeroChat } from "@/components/HeroChat";


interface VideoRecord {
  id: string;
  title: string | null;
  video_url: string | null;
  created_at: string;
  is_featured?: boolean;
  display_order?: number;
}

const Index = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [completedVideos, setCompletedVideos] = useState<VideoRecord[]>([]);
  
  // ── Hero istatistikleri (sahte ama tutarlı/inandırıcı) ──
  const [videoLimit, setVideoLimit] = useState(12);
  const [visibleCount, setVisibleCount] = useState(6);
  const VIDEOS_PER_PAGE = 6;

  // Fetch videos function — reads the public-safe view (videos_showcase)
  // so that product metadata (name, description, image, prompt) is never
  // exposed to unauthenticated visitors.
  const fetchVideos = useCallback(async () => {
    const { data, error } = await (supabase as any)
      .from("videos_showcase")
      .select("id, title, video_url, created_at, is_featured, display_order")
      .order("is_featured", { ascending: false })
      .order("display_order", { ascending: true })
      .order("created_at", { ascending: false });

    if (data && !error) {
      setCompletedVideos(data as VideoRecord[]);
    }
  }, []);

  // Fetch initial data and set up realtime subscription
  useEffect(() => {
    const fetchData = async () => {
      // Fetch site settings
      const { data: settings } = await supabase
        .from("site_settings")
        .select("*")
        .eq("setting_key", "homepage_video_limit")
        .maybeSingle();
      
      if (settings) {
        setVideoLimit(parseInt(settings.setting_value) || 12);
      }

      // Fetch videos
      await fetchVideos();
    };

    fetchData();

    // Set up realtime subscription for videos table
    const channel = supabase
      .channel('homepage-videos')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'videos'
        },
        () => {
          // Refetch videos when any change occurs
          fetchVideos();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchVideos]);

  // All videos from database, limited. poster and prompt are intentionally
  // omitted — the public view does not expose product_image_url or prompt.
  // LazyVideoCard falls back to the video's first frame for its thumbnail
  // and lazily generates a fresh prompt via edge function when visible.
  const allVideos = completedVideos.slice(0, videoLimit).map(v => ({
    id: v.id,
    video_url: v.video_url,
    poster: undefined,
    prompt: null,
    hasPrompt: false,
    productName: v.title || "UGC Video",
  }));

  // Visible videos based on current visibleCount
  const visibleVideos = allVideos.slice(0, visibleCount);
  const hasMoreVideos = visibleCount < allVideos.length;

  const handleLoadMore = () => {
    setVisibleCount(prev => Math.min(prev + VIDEOS_PER_PAGE, allVideos.length));
  };


  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <SEO 
        title="UGC Video Oluşturucu | Ücretsiz AI Video Oluşturucu Türkçe"
        description="Ücretsiz UGC video oluşturucu ile AI destekli video reklamları oluşturun. UGC video örnekleri, UGC video indirme ve profesyonel UGC video reklamları için en iyi uygulama."
        keywords="ugc video, ugc video ücretsiz, ugc video oluşturucu, ugc video örnekleri, ugc video indirme, ugc video uygulaması, ai ugc video oluşturucu ücretsiz, ugc video reklamları, ugc video ai oluşturucu, yapay zeka video oluşturucu, e-ticaret video reklamları, instagram reels video, tiktok reklamları, ürün tanıtım videosu, kullanıcı tarafından oluşturulan içerik"
      />
      <StructuredData type="software" />
      <StructuredData type="organization" />
      <StructuredData type="howto" />
      <WebsiteSchema />
      <BreadcrumbSchema items={[
        { name: "Ana Sayfa", url: "/" }
      ]} />
      <Navbar />

      <main>
      {/* Hero Section — SATIŞ ODAKLI: en üstte Sohbetle Üret (prompt bar) + araç seçenekleri.
          Mobil-öncelikli büyük başlık; palet/tema AYNEN korunur (onaylı mockup, 2026-07-14). */}
      <section className="relative pt-24 md:pt-32 pb-10 md:pb-16 px-4 overflow-hidden">
        {/* Yumuşak marka ışıması (arka plan, açık temada zarif) */}
        <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-[480px] bg-[radial-gradient(55%_60%_at_50%_0%,hsl(346_77%_50%/0.10),transparent_70%),radial-gradient(45%_55%_at_78%_10%,hsl(24_100%_50%/0.08),transparent_70%)]" />
        <div className="container mx-auto max-w-6xl relative">
          <div className="text-center space-y-4 md:space-y-5">
            <div className="inline-flex items-center gap-2 px-3 md:px-4 py-1.5 md:py-2 rounded-full bg-primary/10 border border-primary/20">
              <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-500 opacity-60" /><span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" /></span>
              <span className="text-xs md:text-sm font-medium text-primary-dark">Türkçe yapay zekâ reklam stüdyosu · İlk videon ücretsiz</span>
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold leading-[1.05] tracking-tight px-2" style={{ textWrap: "balance" } as React.CSSProperties}>
              Ürününü Ver, <span className="gradient-text">Reklamın</span> Dakikalar İçinde Hazır Olsun
            </h1>

            <p className="text-base md:text-xl text-muted-foreground max-w-2xl mx-auto px-4">
              Türkçe konuşan avatarlı UGC video, sinema reklamı, ürün fotoğrafı, sanal deneme ve <span className="font-semibold text-foreground">20+ araç tek panelde</span>. Ajans yok, çekim yok, prodüksiyon yok — <span className="font-semibold text-foreground">ilk videon ücretsiz</span>, kredi kartı gerekmez.
            </p>

            {/* EN ÜSTTE: Sohbetle Üret — ana eylem (yaz → AI reklamını üretsin) */}
            {/* Sohbet ANA SAYFADA gerçekten çalışır (yazı+mikrofon+foto); senaryo hazır olunca
                sohbet Stüdyo'ya taşınır. Misafir yazmaya kalkınca kibar giriş uyarısı alır. */}
            <HeroChat />
            <p className="text-xs md:text-sm text-muted-foreground">ya da doğrudan bir araç seç ↓</p>

            {/* Araç seçenekleri — her biri ilgili üretim sayfasına gider */}
            <div className="mx-auto grid max-w-3xl grid-cols-2 gap-2 px-2 sm:grid-cols-3 md:grid-cols-5 sm:gap-2.5">
              {([
                ["🎬 UGC Video", "/results?mode=ugc"],
                ["🎞️ Sinema Reklamı", "/results?mode=cinema"],
                ["🗣️ Konuşan Foto", "/results?mode=talking"],
                ["📸 Ürün Fotoğrafı", "/results?mode=photo"],
                ["👗 Sanal Deneme", "/results?tool=deneme"],
                ["🏗️ Dönüşüm", "/results?mode=donusum"],
                ["🔄 Ürün 360°", "/results?mode=urun360"],
                ["✨ Canlandırma", "/results?mode=canlandir"],
                ["🧩 Parçalara Ayır", "/results?tool=parcala"],
                ["🎨 Logo", "/results?mode=logo"],
              ] as const).map(([label, path]) => (
                <button
                  key={path}
                  type="button"
                  onClick={() => {
                    if (user) { navigate(path); return; }
                    toast.info("Bu aracı kullanmak için ücretsiz kayıt ol / giriş yap 🎁 İlk videon bizden.");
                    navigate("/auth?mode=signup", { state: { from: path } }); // girişten sonra doğrudan araca gider
                  }}
                  className="rounded-xl border border-border bg-card px-2.5 py-2.5 text-xs sm:text-sm font-semibold shadow-soft transition hover:-translate-y-0.5 hover:border-primary/50 hover:text-primary"
                >
                  {label}
                </button>
              ))}
            </div>

            <Button variant="outline" size="sm" className="mt-1" onClick={() => navigate("/pricing")}>Fiyatları Gör</Button>
          </div>
        </div>
      </section>

      {/* Admin-yönetimli tanıtım şeridi (aktif banner varsa görünür, yoksa hiç render olmaz) */}
      <BannerStrip />

      {/* Example UGC Videos Section */}
      <section className="py-8 md:py-12 lg:py-20 overflow-hidden bg-muted/30 relative">
        <div className="container mx-auto px-3 sm:px-4">
          <div className="text-center mb-6 md:mb-10">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold px-2">
              Bu videoların hepsi <span className="gradient-text">sistemimizle</span> üretildi
            </h2>
            <p className="text-sm md:text-base text-muted-foreground mt-2 max-w-xl mx-auto px-4">
              Sen de ürün görselini yükle, dakikalar içinde profesyonel UGC videonu oluştur — izlemekle kalma, dene.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6 max-w-6xl mx-auto">
            {visibleVideos.map((video, index) => (
              <LazyVideoCard
                key={`${video.id}-${index}`}
                id={video.id}
                video_url={video.video_url}
                poster={video.poster}
                prompt={video.prompt}
                hasPrompt={video.hasPrompt}
                index={index}
                productName={video.productName}
              />
            ))}
          </div>
          
          {hasMoreVideos && (
            <div className="flex justify-center mt-6 md:mt-8">
              <Button
                variant="outline"
                size="default"
                onClick={handleLoadMore}
                className="gap-2 text-sm md:text-base px-4 md:px-6"
              >
                Daha Fazla Göster
                <ChevronDown className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Güven göstergeleri hero'ya taşındı (onaylı yeni düzen) — burada tekrarı kaldırıldı. */}


      {/* Features Section */}
      <section className="py-12 md:py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 md:mb-4">
              Neden binlerce marka <span className="gradient-text">bizi seçiyor?</span>
            </h2>
            <p className="text-sm md:text-lg text-muted-foreground max-w-2xl mx-auto px-2">
              UGC video oluşturucu ile reklam üretmek üç adımdan uzun sürmez — dene, gör, ikna ol.
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-8">
            <div className="text-center space-y-3 sm:space-y-4 p-4 sm:p-6 rounded-lg bg-card border border-border hover:shadow-glow transition-all">
              <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary/10">
                <Zap className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold">İlk Videon Ücretsiz</h3>
              <p className="text-sm sm:text-base text-muted-foreground">
                Kredi kartı gerekmez — kayıt ol, ürününü yükle, ilk reklam videonu dakikalar içinde ücretsiz üret ve indir.
              </p>
            </div>
            
            <div className="text-center space-y-3 sm:space-y-4 p-4 sm:p-6 rounded-lg bg-card border border-border hover:shadow-glow transition-all">
              <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary/10">
                <Sparkles className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold">20+ Araç, Tek Panel</h3>
              <p className="text-sm sm:text-base text-muted-foreground">
                Avatarlı tanıtımdan sinema reklamına, sanal denemeden kampanya afişine — aklına gelen her reklam içeriği tek yerde.
              </p>
            </div>
            
            <div className="text-center space-y-3 sm:space-y-4 p-4 sm:p-6 rounded-lg bg-card border border-border hover:shadow-glow transition-all sm:col-span-2 md:col-span-1">
              <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary/10">
                <Target className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold">Tamamen Türkçe</h3>
              <p className="text-sm sm:text-base text-muted-foreground">
                Arayüz, seslendirme, destek — hepsi ana dilinde. Türk tüketicisine doğal gelen, güven veren reklamlar.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-12 md:py-20 px-4 bg-muted/30">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-4 md:mb-6 px-4">
            UGC Video Nasıl Oluşturulur? <span className="gradient-text">3 Adımda Ücretsiz</span>
          </h2>
          <p className="text-center text-sm md:text-lg text-muted-foreground max-w-3xl mx-auto mb-10 md:mb-16 px-4">
            Teknik bilgi gerekmez — ürününü tanıt, gerisini yapay zeka halleder. İlk denemen ücretsiz.
          </p>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-12 h-12 md:w-16 md:h-16 bg-gradient-primary rounded-full flex items-center justify-center text-white font-bold text-xl md:text-2xl shadow-glow">
                1
              </div>
              <div className="bg-card p-6 md:p-8 rounded-xl border border-border hover:border-primary/50 transition-all h-full pt-8 md:pt-10">
                <h3 className="text-lg md:text-xl font-bold mb-3 md:mb-4">Ürün Bilgilerini Girin</h3>
                <p className="text-sm md:text-base text-muted-foreground">
                  E-ticaret ürününüzün adını, özelliklerini ve görselini yükleyin. Sistemimiz ürününüzü analiz eder ve en uygun içerik stratejisini belirler.
                </p>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -top-4 -left-4 w-12 h-12 md:w-16 md:h-16 bg-gradient-primary rounded-full flex items-center justify-center text-white font-bold text-xl md:text-2xl shadow-glow">
                2
              </div>
              <div className="bg-card p-6 md:p-8 rounded-xl border border-border hover:border-primary/50 transition-all h-full pt-8 md:pt-10">
                <h3 className="text-lg md:text-xl font-bold mb-3 md:mb-4">AI Video Üretimi Başlar</h3>
                <p className="text-sm md:text-base text-muted-foreground">
                  Yapay zeka teknolojimiz, ürününüz için otantik ve ikna edici kullanıcı tarafından oluşturulan içerik videoları hazırlar. Her video markanıza özel optimize edilir.
                </p>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -top-4 -left-4 w-12 h-12 md:w-16 md:h-16 bg-gradient-primary rounded-full flex items-center justify-center text-white font-bold text-xl md:text-2xl shadow-glow">
                3
              </div>
              <div className="bg-card p-6 md:p-8 rounded-xl border border-border hover:border-primary/50 transition-all h-full pt-8 md:pt-10">
                <h3 className="text-lg md:text-xl font-bold mb-3 md:mb-4">UGC Video İndir ve Paylaş</h3>
                <p className="text-sm md:text-base text-muted-foreground">
                  Hazır reklamını tek tıkla indir; Instagram, TikTok ve reklam kampanyalarında hemen kullan. Çıktıların Stüdyonda kalıcı saklanır.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section - Infinite Scroll */}
      <section className="py-12 md:py-20 overflow-hidden">
        <div className="container mx-auto max-w-6xl px-4 mb-10 md:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-4 md:mb-6">
            Müşterilerimiz <span className="gradient-text">Ne Diyor?</span>
          </h2>
          <p className="text-center text-sm md:text-lg text-muted-foreground max-w-3xl mx-auto">
            Binlerce e-ticaret markası UGC Studio ile videoları ile satışlarını artırdı
          </p>
        </div>

        {/* Auto-scrolling testimonials */}
        <div className="relative">
          <div className="flex gap-6 animate-scroll-left">
            {(() => {
              const avatarColors = [
                "bg-gradient-to-br from-pink-500 to-rose-500",
                "bg-gradient-to-br from-violet-500 to-purple-500",
                "bg-gradient-to-br from-blue-500 to-cyan-500",
                "bg-gradient-to-br from-emerald-500 to-teal-500",
                "bg-gradient-to-br from-orange-500 to-amber-500",
                "bg-gradient-to-br from-red-500 to-pink-500",
                "bg-gradient-to-br from-indigo-500 to-blue-500",
                "bg-gradient-to-br from-teal-500 to-green-500",
                "bg-gradient-to-br from-fuchsia-500 to-pink-500",
                "bg-gradient-to-br from-cyan-500 to-blue-500",
                "bg-gradient-to-br from-lime-500 to-green-500",
                "bg-gradient-to-br from-amber-500 to-yellow-500",
              ];
              
              const testimonials = [
                { initials: "AY", name: "Ayşe Yılmaz", role: "Kozmetik E-ticaret Sahibi", rating: 5, text: "UGC Studio sayesinde reklam videolarımı artık dakikalar içinde hazırlıyorum. Dönüşüm oranlarım %120 arttı!" },
                { initials: "MK", name: "Mehmet Kaya", role: "Moda Markası Kurucusu", rating: 5, text: "Instagram Reels için her gün içerik üretmek zorundaydım. AI ile artık çok daha hızlı ve profesyonel videolar çıkarıyorum." },
                { initials: "ZD", name: "Zeynep Demir", role: "Dijital Pazarlama Uzmanı", rating: 5, text: "Müşterilerim için UGC içerikler üretiyorum. Bu platform sayesinde zamandan ve maliyetten %70 tasarruf ediyorum." },
                { initials: "EC", name: "Emre Can", role: "Elektronik Mağaza Sahibi", rating: 5, text: "Ürün tanıtım videoları için artık profesyonel ekip tutmuyorum. Tek başıma harika içerikler üretebiliyorum." },
                { initials: "SÖ", name: "Selin Öztürk", role: "Ev Dekorasyon Markası", rating: 5, text: "Sosyal medya reklamlarımda CTR oranım %200 arttı. Müşteriler gerçek kullanıcı videolarına çok daha iyi tepki veriyor." },
                { initials: "AK", name: "Ali Korkmaz", role: "Spor Malzemeleri Satıcısı", rating: 5, text: "Video prodüksiyon maliyetlerim %80 düştü. Kalite ise aynı seviyede kaldı hatta daha da iyileşti." },
                { initials: "DY", name: "Deniz Yıldız", role: "Gıda E-ticaret", rating: 5, text: "Ürünlerimi tanıtmak için mükemmel bir araç. Müşterilerimden çok olumlu geri dönüşler alıyorum." },
                { initials: "BT", name: "Burak Tekin", role: "Teknoloji Girişimcisi", rating: 5, text: "Startup'ımızın pazarlama bütçesi kısıtlıydı. Bu platform ile profesyonel reklam videoları üretmeye başladık." },
                { initials: "NC", name: "Nur Çelik", role: "Butik Mağaza Sahibi", rating: 5, text: "Her yeni ürün için video üretmek artık çok kolay. Müşterilerim videoları çok beğeniyor." },
                { initials: "HY", name: "Hakan Yalçın", role: "Otomotiv Aksesuarları", rating: 5, text: "Ürün demolarımı video ile göstermek satışlarımı katladı. Kesinlikle yatırıma değer." },
                { initials: "GA", name: "Gül Arslan", role: "Anne-Bebek Ürünleri", rating: 5, text: "Güvenilir ve samimi UGC videoları ile hedef kitleme ulaşmak çok kolaylaştı." },
                { initials: "ÖS", name: "Özlem Şahin", role: "El Yapımı Takı Tasarımcısı", rating: 5, text: "Takılarımı sergileyen videolar artık dakikalar içinde hazır. Etsy satışlarım %150 arttı." },
                { initials: "KE", name: "Kerem Erdoğan", role: "Outdoor & Kamp", rating: 5, text: "Kamp ekipmanlarımızı kullanan kişilerin deneyimlerini gösteren videolar müthiş sonuçlar veriyor." },
                { initials: "İA", name: "İrem Aydın", role: "Sağlıklı Yaşam Koçu", rating: 5, text: "Online eğitimlerimi tanıtmak için kullanıyorum. Kayıt oranlarım belirgin şekilde arttı." },
                { initials: "TK", name: "Tolga Kılıç", role: "Pet Shop Sahibi", rating: 5, text: "Evcil hayvan ürünlerimizi tanıtan sevimli videolar oluşturuyoruz. Müşteriler bayılıyor!" },
                { initials: "FA", name: "Fatma Akgün", role: "Organik Kozmetik", rating: 5, text: "Doğal ürünlerimizi tanıtan içerikler marka bilinirliğimizi çok artırdı." },
                { initials: "CY", name: "Cem Yaman", role: "Mobilya Mağazası", rating: 5, text: "Mobilya montaj ve kullanım videoları müşteri memnuniyetini artırdı, iade oranları düştü." },
                { initials: "EK", name: "Elif Koç", role: "Çanta & Aksesuar", rating: 5, text: "Her yeni koleksiyonda video içerikleri ile lansman yapıyorum. Sonuçlar muhteşem!" },
                { initials: "MT", name: "Murat Tuncer", role: "Kahve Dükkanı Zinciri", rating: 5, text: "Yeni ürünlerimizi tanıtan UGC videolar sosyal medyada viral oluyor." },
                { initials: "AS", name: "Aylin Sarı", role: "Yoga Stüdyosu", rating: 5, text: "Online derslerime kayıt yaptıran öğrenci sayısı %90 arttı." },
                { initials: "YB", name: "Yusuf Bal", role: "Hobi Malzemeleri", rating: 5, text: "DIY projelerini anlatan videolar müşterilerimize ilham veriyor ve satışları artırıyor." },
                { initials: "PD", name: "Pınar Doğan", role: "Çocuk Giyim", rating: 5, text: "Anneler olarak birbirimize önerdiğimiz ürünler gibi görünen videolar harika sonuç veriyor." },
                { initials: "OK", name: "Okan Kara", role: "Bahçe & Peyzaj", rating: 5, text: "Bahçe ürünlerinin nasıl kullanıldığını gösteren videolar müşteri sorularını azalttı." },
                { initials: "SY", name: "Sibel Yıldırım", role: "Güzellik Salonu", rating: 5, text: "Hizmetlerimizi tanıtan profesyonel videolar ile randevu taleplerinde patlama yaşadık." }
              ];
              
              // Duplicate for seamless loop
              const allTestimonials = [...testimonials, ...testimonials.slice(0, 6)];
              
              return allTestimonials.map((testimonial, idx) => (
                <div 
                  key={idx} 
                  className="flex-shrink-0 w-[280px] sm:w-[320px] md:w-[380px] bg-card p-4 sm:p-6 rounded-xl border-2 border-border/50 hover:border-primary/70 shadow-lg hover:shadow-xl hover:shadow-primary/10 transition-all duration-300"
                >
                  <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full ${avatarColors[idx % avatarColors.length]} flex items-center justify-center text-white font-bold text-xs sm:text-sm shadow-md`}>
                      {testimonial.initials}
                    </div>
                    <div>
                      <div className="font-semibold text-sm sm:text-base text-foreground">{testimonial.name}</div>
                      <div className="text-xs sm:text-xs text-muted-foreground">{testimonial.role}</div>
                    </div>
                  </div>
                  <div className="flex gap-0.5 mb-2 sm:mb-3">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <span key={i} className="text-yellow-500 text-xs sm:text-sm">⭐</span>
                    ))}
                  </div>
                  <p className="text-xs sm:text-sm text-muted-foreground italic leading-relaxed">
                    "{testimonial.text}"
                  </p>
                </div>
              ));
            })()}
          </div>
        </div>

        {/* CTA after testimonials */}
        <div className="container mx-auto max-w-6xl px-4">
          <div className="text-center mt-10 md:mt-12">
            <Button 
              variant="hero" 
              size="lg"
              onClick={() => navigate(user ? "/pricing" : "/auth?mode=signup")}
              className="text-sm md:text-base hover-scale"
            >
              Siz de Başarı Hikayenizi Yazın
            </Button>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 md:py-20 px-4 bg-gradient-primary text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12 text-center">
            <div className="space-y-2 animate-fade-in">
              <div className="text-4xl md:text-5xl lg:text-6xl font-bold">10K+</div>
              <div className="text-sm md:text-base text-white/90">Oluşturulan Video</div>
            </div>
            <div className="space-y-2 animate-fade-in" style={{ animationDelay: "0.1s" }}>
              <div className="text-4xl md:text-5xl lg:text-6xl font-bold">%85</div>
              <div className="text-sm md:text-base text-white/90">Daha Fazla Dönüşüm</div>
            </div>
            <div className="space-y-2 animate-fade-in" style={{ animationDelay: "0.2s" }}>
              <div className="text-4xl md:text-5xl lg:text-6xl font-bold">4.8★</div>
              <div className="text-sm md:text-base text-white/90">Kullanıcı Puanı</div>
            </div>
            <div className="space-y-2 animate-fade-in" style={{ animationDelay: "0.3s" }}>
              <div className="text-4xl md:text-5xl lg:text-6xl font-bold">~7.5dk</div>
              <div className="text-sm md:text-base text-white/90">Ortalama Üretim Süresi</div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 md:py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-4 md:mb-6 px-4">
            UGC Video Reklamları ile <span className="gradient-text">Satışları Artırın</span>
          </h2>
          <p className="text-center text-sm md:text-lg text-muted-foreground max-w-3xl mx-auto mb-10 md:mb-16 px-4">
            Gerçek kullanıcı deneyimi gibi görünen reklamlar, geleneksel reklamlardan belirgin şekilde daha çok satar. İşte nedenleri:
          </p>

          <div className="grid sm:grid-cols-2 gap-6 md:gap-8">
            <div className="bg-card/50 backdrop-blur p-6 md:p-8 rounded-xl border border-border hover:border-primary/50 transition-all">
              <h3 className="text-lg md:text-xl font-bold mb-3 md:mb-4 text-primary">📈 Dönüşüm Oranlarını Artırır</h3>
              <p className="text-sm md:text-base text-muted-foreground">
                UGC video reklamlar, geleneksel reklam içeriklerine göre %85 daha yüksek dönüşüm oranı sağlar. Müşteriler, gerçek kullanıcı deneyimlerini görmeyi tercih eder.
              </p>
            </div>

            <div className="bg-card/50 backdrop-blur p-6 md:p-8 rounded-xl border border-border hover:border-primary/50 transition-all">
              <h3 className="text-lg md:text-xl font-bold mb-3 md:mb-4 text-primary">💰 Maliyet Etkin Çözüm</h3>
              <p className="text-sm md:text-base text-muted-foreground">
                Profesyonel video prodüksiyon ekiplerine binlerce lira ödemek yerine, AI ile dakikalar içinde profesyonel kalitede UGC videoları oluşturun.
              </p>
            </div>

            <div className="bg-card/50 backdrop-blur p-6 md:p-8 rounded-xl border border-border hover:border-primary/50 transition-all">
              <h3 className="text-lg md:text-xl font-bold mb-3 md:mb-4 text-primary">🎯 Hedef Kitle Güveni</h3>
              <p className="text-sm md:text-base text-muted-foreground">
                Kullanıcı tarafından oluşturulan içerik, marka reklamlarına göre 2.4 kat daha güvenilir görülür. Otantik içeriklerle hedef kitlenizle güçlü bağlar kurun.
              </p>
            </div>

            <div className="bg-card/50 backdrop-blur p-6 md:p-8 rounded-xl border border-border hover:border-primary/50 transition-all">
              <h3 className="text-lg md:text-xl font-bold mb-3 md:mb-4 text-primary">⚡ Dakikalar İçinde Hazır</h3>
              <p className="text-sm md:text-base text-muted-foreground">
                Ajans süreçleri haftalar sürer; burada ürün fotoğrafını yüklersin, ortalama 7.5 dakikada reklamın elinde olur.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-12 md:py-20 px-4 bg-muted/30">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-4 md:mb-6 px-4">
            <span className="gradient-text">UGC Video Oluşturucu</span> Kullanım Alanları
          </h2>
          <p className="text-center text-sm md:text-lg text-muted-foreground max-w-3xl mx-auto mb-10 md:mb-16 px-4">
            Kozmetikten elektroniğe, modadan ev yaşamına — her sektör için doğru reklam formatı burada.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            <div className="bg-card p-6 md:p-8 rounded-xl border border-border hover:shadow-glow transition-all">
              <div className="text-3xl md:text-4xl mb-3 md:mb-4">🛍️</div>
              <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3">UGC Video Reklamları</h3>
              <p className="text-sm md:text-base text-muted-foreground">
                UGC video oluşturucu ile profesyonel UGC video reklamları hazırlayın. UGC video ücretsiz indirin ve kullanın.
              </p>
            </div>

            <div className="bg-card p-6 md:p-8 rounded-xl border border-border hover:shadow-glow transition-all">
              <div className="text-3xl md:text-4xl mb-3 md:mb-4">📱</div>
              <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3">UGC Video Uygulaması</h3>
              <p className="text-sm md:text-base text-muted-foreground">
                Instagram Reels ve TikTok için 9:16 dikey, scroll durduran videolar — platforma özel optimize edilir.
              </p>
            </div>

            <div className="bg-card p-6 md:p-8 rounded-xl border border-border hover:shadow-glow transition-all">
              <div className="text-3xl md:text-4xl mb-3 md:mb-4">🎬</div>
              <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3">Reklam Kampanyaları</h3>
              <p className="text-sm md:text-base text-muted-foreground">
                Google Ads, Meta Ads ve diğer platformlar için performans odaklı UGC video reklamlar hazırlayın.
              </p>
            </div>

            <div className="bg-card p-6 md:p-8 rounded-xl border border-border hover:shadow-glow transition-all">
              <div className="text-3xl md:text-4xl mb-3 md:mb-4">💄</div>
              <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3">Kozmetik ve Moda</h3>
              <p className="text-sm md:text-base text-muted-foreground">
                Güzellik ürünleri ve moda markaları için influencer tarzı UGC içerikler oluşturun.
              </p>
            </div>

            <div className="bg-card p-6 md:p-8 rounded-xl border border-border hover:shadow-glow transition-all">
              <div className="text-3xl md:text-4xl mb-3 md:mb-4">🏠</div>
              <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3">Ev ve Yaşam</h3>
              <p className="text-sm md:text-base text-muted-foreground">
                Ev ürünleri, dekorasyon ve lifestyle kategorisinde gerçekçi kullanıcı deneyim videoları üretin.
              </p>
            </div>

            <div className="bg-card p-6 md:p-8 rounded-xl border border-border hover:shadow-glow transition-all">
              <div className="text-3xl md:text-4xl mb-3 md:mb-4">🎮</div>
              <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3">Teknoloji ve Oyun</h3>
              <p className="text-sm md:text-base text-muted-foreground">
                Elektronik ürünler ve oyunlar için heyecan verici UGC video içerikleri hazırlayın.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 2025 AI Video Trendleri - SEO Section */}
      <section className="py-12 md:py-20 px-4 bg-gradient-to-b from-background to-muted/30">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-10 md:mb-16">
            <Badge variant="outline" className="mb-4 border-primary/30 text-primary">
              <Sparkles className="w-3 h-3 mr-1" />
              2025 Dijital Pazarlama Trendleri
            </Badge>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 md:mb-6 px-4">
              UGC Türkiye'de <span className="gradient-text">Dijital Pazarlama Devrimi</span>
            </h2>
            <p className="text-sm md:text-lg text-muted-foreground max-w-3xl mx-auto px-4">
              Ürün fotoğrafınızdan e-ticaret video reklamı ve Instagram Reels içeriği üretin. 
              UGC video ajansı olarak TikTok reklamları ve ürün tanıtım videosu çözümleri sunuyoruz.
            </p>
          </div>

          {/* Trend Cards */}
          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            <div className="bg-card p-6 md:p-8 rounded-xl border border-border hover:border-primary/50 transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-xl">🎯</span>
                </div>
                <h3 className="text-lg md:text-xl font-bold">Instagram Reels ve TikTok Reklamları</h3>
              </div>
              <p className="text-sm md:text-base text-muted-foreground mb-4">
                Kısa form video içerikleri 2025'te en etkili pazarlama aracı. AI destekli UGC video oluşturucu ile 
                TikTok, Instagram Reels ve YouTube Shorts için scroll-durduran içerikler üretin. 
                Yapay zeka, viral potansiyeli yüksek formatları otomatik olarak optimize eder.
              </p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <span className="text-primary">✓</span>
                  9:16 dikey format optimizasyonu
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-primary">✓</span>
                  Türkçe altyazı ve seslendirme
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-primary">✓</span>
                  Trend ses ve efekt entegrasyonu
                </li>
              </ul>
            </div>

            <div className="bg-card p-6 md:p-8 rounded-xl border border-border hover:border-primary/50 transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-xl">🛒</span>
                </div>
                <h3 className="text-lg md:text-xl font-bold">E-ticaret Video Reklamları Optimizasyonu</h3>
              </div>
              <p className="text-sm md:text-base text-muted-foreground mb-4">
                Geleneksel ürün fotoğrafları artık yeterli değil. UGC video reklamlar, potansiyel müşterilere 
                ürünü gerçek kullanıcı deneyimi olarak sunar. Trendyol, Hepsiburada ve Shopify mağazalarınız için 
                satış odaklı video içerikleri saniyeler içinde hazırlayın.
              </p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <span className="text-primary">✓</span>
                  Ürün inceleme videoları
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-primary">✓</span>
                  Kutu açılımı (Unboxing) içerikleri
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-primary">✓</span>
                  Karşılaştırma ve test videoları
                </li>
              </ul>
            </div>

            <div className="bg-card p-6 md:p-8 rounded-xl border border-border hover:border-primary/50 transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-xl">🤖</span>
                </div>
                <h3 className="text-lg md:text-xl font-bold">UGC İçerik Üretimi ile Video Oluşturma</h3>
              </div>
              <p className="text-sm md:text-base text-muted-foreground mb-4">
                2025'te influencer maliyetleri olmadan otantik içerikler üretin. AI video oluşturucu teknolojimiz, 
                markanız için kişiselleştirilmiş, güvenilir görünen UGC formatında videolar yaratır. 
                Türkçe dil desteği ile yerel pazara özel içerikler.
              </p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <span className="text-primary">✓</span>
                  Sınırsız içerik ölçeklendirme
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-primary">✓</span>
                  A/B test için çoklu varyasyon
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-primary">✓</span>
                  Marka tutarlılığı garantisi
                </li>
              </ul>
            </div>

            <div className="bg-card p-6 md:p-8 rounded-xl border border-border hover:border-primary/50 transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-xl">📈</span>
                </div>
                <h3 className="text-lg md:text-xl font-bold">Reklam Performansı Artışı</h3>
              </div>
              <p className="text-sm md:text-base text-muted-foreground mb-4">
                Meta Ads, Google Ads ve TikTok Ads kampanyalarınızda UGC video kullanımı ROAS'ınızı katlar. 
                Araştırmalar, kullanıcı tarafından oluşturulan içerik reklamlarının geleneksel reklamlara göre 
                %4x daha fazla tıklama aldığını gösteriyor.
              </p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <span className="text-primary">✓</span>
                  Düşük CPC ve CPM oranları
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-primary">✓</span>
                  Yüksek reklam kalite puanı
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-primary">✓</span>
                  Organik görünümlü reklam formatı
                </li>
              </ul>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center mt-10 md:mt-12">
            <p className="text-sm md:text-base text-muted-foreground mb-4">
              2025'te rakiplerinizin önüne geçin. UGC video oluşturucu ile hemen başlayın.
            </p>
            <Button 
              variant="hero" 
              size="default"
              onClick={() => navigate(user ? "/results" : "/auth?mode=signup")}
              className="gap-2 w-full sm:w-auto"
            >
              <Sparkles className="w-4 h-4" />
              Ücretsiz Video Oluştur
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 md:py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-4 md:mb-6 px-4">
            UGC Video Oluşturucu Hakkında <span className="gradient-text">Sıkça Sorulan Sorular</span>
          </h2>
          <p className="text-center text-sm md:text-lg text-muted-foreground mb-10 md:mb-12 px-4">
            Ücretsiz deneme, video örnekleri ve platform hakkında en çok merak edilenler
          </p>

          <div className="space-y-4 md:space-y-6">
            <div className="bg-card p-6 md:p-8 rounded-xl border border-border">
              <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3">UGC video nedir ve UGC video örnekleri nelerdir?</h3>
              <p className="text-sm md:text-base text-muted-foreground">
                UGC video, "kullanıcı tarafından oluşturulmuş içerik" tarzında çekilmiş, samimi ve güven veren tanıtım videosudur — ürün incelemesi, kutu açılımı, deneyim paylaşımı gibi. Platformumuzda yapay zeka bu videoları senin yerine, Türkçe konuşan gerçekçi avatarlarla üretir.
              </p>
            </div>

            <div className="bg-card p-6 md:p-8 rounded-xl border border-border">
              <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3">UGC video ücretsiz nasıl oluşturulur?</h3>
              <p className="text-sm md:text-base text-muted-foreground">
                Ücretsiz kayıt ol, ürününün fotoğrafını ya da linkini yükle, stilini seç — yapay zeka Türkçe konuşan reklam videonu dakikalar içinde hazırlar. İlk videon tamamen ücretsizdir, kredi kartı gerekmez.
              </p>
            </div>

            <div className="bg-card p-6 md:p-8 rounded-xl border border-border">
              <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3">UGC video reklamlar hangi platformlarda kullanılabilir?</h3>
              <p className="text-sm md:text-base text-muted-foreground">
                Oluşturduğunuz UGC videoları tüm sosyal medya platformlarında (Instagram, TikTok, Facebook, YouTube), reklam ağlarında (Google Ads, Meta Ads) ve e-ticaret sitelerinizde kullanabilirsiniz. Videolarımız tüm platformlar için optimize edilmiştir.
              </p>
            </div>

            <div className="bg-card p-6 md:p-8 rounded-xl border border-border">
              <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3">UGC video indirme nasıl yapılır?</h3>
              <p className="text-sm md:text-base text-muted-foreground">
                Üretilen her video Stüdyondaki listene kalıcı kaydedilir; tek tıkla indirir, dilersen uygulama içi video editörle kesip müzik ekleyerek yayınlarsın.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-gradient-primary rounded-2xl md:rounded-3xl p-8 md:p-12 text-center text-white shadow-glow">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-6">
              Hemen Başlayın
            </h2>
            <p className="text-base md:text-xl mb-6 md:mb-8 text-white/90">
              UGC içeriklerinizi oluşturmaya bugün başlayın
            </p>
            <Button 
              size="default" 
              variant="outline"
              onClick={() => navigate(user ? "/results" : "/auth?mode=signup")}
              className="text-sm md:text-base w-full sm:w-auto bg-white text-primary border-white hover:bg-white/90 hover:text-primary font-semibold"
            >
              {user ? "Video Oluştur" : "Ücretsiz Dene"}
            </Button>
          </div>
        </div>
      </section>
      </main>
      <Footer />
    </div>
  );
};

export default Index;
